///
/// EarthBrowser
///
/// Created by Matt Giger on 1/11/2026
/// Copyright (c) 2026 Geodata Labs
///

import { MeasurementType, shouldUseMetric } from "./UnitSystem"

// Conversion constants
const METERS_TO_FEET = 3.28084
const KM_TO_MILES = 0.621371
const MPS_TO_MPH = 2.23694
const MPS_TO_KNOTS = 1.94384

/**
 * Convert Celsius to Fahrenheit
 */
function celsiusToFahrenheit(celsius: number): number {
	return (celsius * 9/5) + 32
}

/**
 * Format temperature with locale-appropriate units
 * @param celsius - Temperature in Celsius
 * @param decimals - Number of decimal places (default: 1)
 */
export function formatTemperature(celsius: number, decimals = 1): string {
	if (shouldUseMetric(MeasurementType.TEMPERATURE)) {
		return `${celsius.toFixed(decimals)}°C`
	}
	return `${celsiusToFahrenheit(celsius).toFixed(decimals)}°F`
}

/**
 * Format altitude/elevation with locale-appropriate units
 * Metric: meters or kilometers
 * US Customary: feet
 * @param meters - Altitude in meters
 * @param useKmForLarge - Use km when > 1000m in metric (default: false for elevation)
 */
export function formatAltitude(meters: number, useKmForLarge = false): string {
	if (shouldUseMetric(MeasurementType.ALTITUDE)) {
		if (useKmForLarge && Math.abs(meters) >= 1000) {
			return `${(meters / 1000).toFixed(1)} km`
		}
		return `${meters.toLocaleString(undefined, { maximumFractionDigits: 0 })} m`
	}
	const feet = meters * METERS_TO_FEET
	return `${feet.toLocaleString(undefined, { maximumFractionDigits: 0 })} ft`
}

/**
 * Format distance with locale-appropriate units
 * Metric: km or meters
 * US Customary: miles or feet
 * @param km - Distance in kilometers
 */
export function formatDistance(km: number): string {
	if (shouldUseMetric(MeasurementType.DISTANCE)) {
		if (km < 1) {
			return `${(km * 1000).toFixed(0)} m`
		}
		return `${km.toFixed(1)} km`
	}
	const miles = km * KM_TO_MILES
	if (miles < 0.1) {
		return `${(km * 1000 * METERS_TO_FEET).toFixed(0)} ft`
	}
	return `${miles.toFixed(1)} mi`
}

/**
 * Format depth (always positive display) with locale-appropriate units
 * @param km - Depth in kilometers
 */
export function formatDepth(km: number): string {
	if (shouldUseMetric(MeasurementType.DISTANCE)) {
		return `${km.toFixed(1)} km`
	}
	const miles = km * KM_TO_MILES
	return `${miles.toFixed(1)} mi`
}

/**
 * Format satellite altitude with locale-appropriate units
 * Always uses km/miles for satellite altitudes
 * @param km - Altitude in kilometers
 */
export function formatSatelliteAltitude(km: number): string {
	if (shouldUseMetric(MeasurementType.ALTITUDE)) {
		return `${km.toFixed(1)} km`
	}
	const miles = km * KM_TO_MILES
	return `${miles.toFixed(1)} mi`
}

/**
 * Format speed with locale-appropriate units
 * Metric: m/s or km/h
 * US Customary: mph
 * @param mps - Speed in meters per second
 * @param useKnots - Use knots for nautical context (optional)
 */
export function formatSpeed(mps: number, useKnots = false): string {
	if (useKnots) {
		const knots = mps * MPS_TO_KNOTS
		return `${knots.toFixed(1)} kn`
	}
	if (shouldUseMetric(MeasurementType.SPEED)) {
		return `${mps.toFixed(1)} m/s`
	}
	const mph = mps * MPS_TO_MPH
	return `${mph.toFixed(1)} mph`
}

/**
 * Format wave height with locale-appropriate units
 * Metric: meters
 * US Customary: feet
 * @param meters - Wave height in meters
 */
export function formatWaveHeight(meters: number): string {
	if (shouldUseMetric(MeasurementType.ALTITUDE)) {
		return `${meters.toFixed(1)} m`
	}
	const feet = meters * METERS_TO_FEET
	return `${feet.toFixed(1)} ft`
}

const ACRES_TO_HECTARES = 0.404686
const ACRES_TO_SQ_KM = 0.00404686
const ACRES_TO_SQ_MILES = 1 / 640

/**
 * Format a fire/burn area with locale-appropriate units
 * Metric: hectares, switching to km² above 10,000 ha
 * US Customary: acres, with square miles added for large fires for scale
 * @param acres - Area in acres
 */
export function formatFireArea(acres: number): string {
	if (shouldUseMetric(MeasurementType.DISTANCE)) {
		const hectares = acres * ACRES_TO_HECTARES
		if (hectares >= 10000) {
			return `${(acres * ACRES_TO_SQ_KM).toLocaleString(undefined, { maximumFractionDigits: 0 })} km²`
		}
		return `${hectares.toLocaleString(undefined, { maximumFractionDigits: 0 })} ha`
	}
	const acresStr = `${acres.toLocaleString(undefined, { maximumFractionDigits: 0 })} acres`
	const sqMiles = acres * ACRES_TO_SQ_MILES
	if (sqMiles >= 5) {
		return `${acresStr} (${sqMiles.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq mi)`
	}
	return acresStr
}

/**
 * Format precipitation amount with locale-appropriate units
 * Metric: millimeters
 * US Customary: inches
 * @param mm - Precipitation in millimeters
 */
export function formatPrecipitation(mm: number): string {
	if (shouldUseMetric(MeasurementType.DISTANCE)) {
		return `${mm.toFixed(1)} mm`
	}
	return `${(mm / 25.4).toFixed(2)} in`
}

/**
 * Convert precipitation value for display (without unit string)
 */
export function convertPrecipitation(mm: number): number {
	if (shouldUseMetric(MeasurementType.DISTANCE)) {
		return Number(mm.toFixed(1))
	}
	return Number((mm / 25.4).toFixed(2))
}

/**
 * Get precipitation unit label
 */
export function getPrecipitationUnit(): string {
	return shouldUseMetric(MeasurementType.DISTANCE) ? "mm" : "in"
}

/**
 * Get temperature unit label
 */
export function getTemperatureUnit(): string {
	return shouldUseMetric(MeasurementType.TEMPERATURE) ? "°C" : "°F"
}

/**
 * Get altitude unit label
 */
export function getAltitudeUnit(): string {
	return shouldUseMetric(MeasurementType.ALTITUDE) ? "m" : "ft"
}

/**
 * Get speed unit label
 */
export function getSpeedUnit(): string {
	return shouldUseMetric(MeasurementType.SPEED) ? "m/s" : "mph"
}

/**
 * Get wave height unit label
 */
export function getWaveHeightUnit(): string {
	return shouldUseMetric(MeasurementType.ALTITUDE) ? "m" : "ft"
}

/**
 * Get distance unit label for km-scale
 */
export function getDistanceUnit(): string {
	return shouldUseMetric(MeasurementType.DISTANCE) ? "km" : "mi"
}

/**
 * Convert temperature value for display (without unit string)
 */
export function convertTemperature(celsius: number, decimals = 1): number {
	if (shouldUseMetric(MeasurementType.TEMPERATURE)) {
		return Number(celsius.toFixed(decimals))
	}
	return Number(celsiusToFahrenheit(celsius).toFixed(decimals))
}

/**
 * Convert wave height value for display (without unit string)
 */
export function convertWaveHeight(meters: number, decimals = 1): number {
	if (shouldUseMetric(MeasurementType.ALTITUDE)) {
		return Number(meters.toFixed(decimals))
	}
	return Number((meters * METERS_TO_FEET).toFixed(decimals))
}

/**
 * Convert speed value for display (without unit string)
 */
export function convertSpeed(mps: number, decimals = 1): number {
	if (shouldUseMetric(MeasurementType.SPEED)) {
		return Number(mps.toFixed(decimals))
	}
	return Number((mps * MPS_TO_MPH).toFixed(decimals))
}
